import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  CircularProgress,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CustomBreadcrumbs from "../../common/components/breadcrumb-component-new/breadcrumb-component-new";
import { useNavigate, useParams } from "react-router-dom";
import {
  getPreneedPlannerDetails,
  requestQuote,
} from "../../api/preneed-planners.api";
import PlannerHeader from "./components/PlannerHeader";
import PlannerDescription from "./components/PlannerDescription";
import PlannerActionButtons from "./components/PlannerActionButtons";
import PlansPackages from "./components/PlansPackages";
import QuoteFormContent from "./components/QuoteForm";
import ServicesWeProvide from "./components/ServicesWeProvide";
import TestimonialSection from "./components/TestimonialSection";
import LocationComponent from "../details-page/components/LocationComponent";
import PlannerImageGallery from "./components/PlannerImageGallery";
import PlannerSuccessModal from "../../common/components/planner-success-modal/PlannerSuccessModal";
import { PlannerDetails } from "./preneed-planners-details-page.types";
import { ArrowBack } from "@mui/icons-material";
import "./preneed-planners-details-page.scss";
import ReviewsComponent from "../details-page/components/ReviewsComponent";
import { getProviderReviewsById } from "../../api/details-page.api";

const PreneedPlannersDetailsPage = () => {
  const { id } = useParams();
  const [plannerDetails, setPlannerDetails] = useState<PlannerDetails | null>(
    null,
  );
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();
  const testimonialsRef = useRef<HTMLDivElement | null>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleReviewsClick = () => {
    reviewsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleBack = () => {
    navigate("/home/list-of-funeral-homes/preneed-planners");
  };
  const scrollToTestimonials = () => {
    testimonialsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const fetchPreneedPlannerDetails = async () => {
    try {
      setErrorMessage(null);
      const storedLocation = localStorage.getItem("user_location");
      let latitude = null;
      let longitude = null;

      if (storedLocation) {
        const loc = JSON.parse(storedLocation);
        latitude = loc.latitude;
        longitude = loc.longitude;
      }
      const [detailsResponse, reviewsResponse] = await Promise.all([
        getPreneedPlannerDetails(Number(id), {
          userLat: latitude,
          userLng: longitude,
        }),
        getProviderReviewsById(Number(id)),
      ]);

      setPlannerDetails(detailsResponse.data.data);

      if (reviewsResponse.data.success) {
        const reviewsData = reviewsResponse.data.data.data || [];
        setReviews(reviewsData);
      }
    } catch (error: any) {
      console.error("ERROR FETCHING PRENEED DETAILS::", error);
      if (error?.response?.data.errorCode === "PRENEED_PLANNER_NOT_FOUND") {
        setErrorMessage("Planner is not serving in the selected location");
      }
    }
  };

  const handleReviewSubmitted = () => {
    fetchPreneedPlannerDetails();
  };
  const handleReviewDeleted = () => {
    fetchPreneedPlannerDetails();
  };

  useEffect(() => {
    fetchPreneedPlannerDetails();
  }, []);

  return (
    <>
      {plannerDetails && (
        <Container className="preneed-planners-details-page">
          <Button
            startIcon={<ArrowBack fontSize="large" />}
            onClick={handleBack}
            sx={{
              textTransform: "none",
              color: "#161616",
              paddingLeft: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
              cursor: "pointer",
            }}
          >
            Back
          </Button>
          {/* Breadcrumb */}
          <Box className="preneed-planners-details-page__breadcrumbs">
            <CustomBreadcrumbs
              items={[
                {
                  label: "List of Planners",
                  to: "/home/list-of-funeral-homes/preneed-planners",
                },
                { label: plannerDetails?.businessName || "" },
              ]}
            />
          </Box>

          {/* Planner Header */}
          <Box className="preneed-planners-details-page__header">
            <PlannerHeader plannerDetails={plannerDetails} />
          </Box>

          {/* Planner Description */}
          <Box className="preneed-planners-details-page__description">
            <PlannerDescription description={plannerDetails?.description} />
          </Box>

          {/* Planner Action Buttons */}
          <Box className="preneed-planners-details-page__actions">
            <PlannerActionButtons
              email={plannerDetails?.email}
              phoneNumber={plannerDetails?.phoneNumber}
              onReviewClick={handleReviewsClick}
            />
          </Box>

          <Box className="preneed-planners-details-page__media">
            <Box className="preneed-planners-details-page__gallery">
              <PlannerImageGallery
                images={plannerDetails?.fileInfo}
                providerTitle={plannerDetails?.businessName}
              />
            </Box>
            <Box className="preneed-planners-details-page__map">
              <LocationComponent
                providerId={plannerDetails?.id}
                type="planner"
                locationInfo={plannerDetails?.locationInfo}
              />
            </Box>
          </Box>
          {/* Services Section */}
          <Box className="preneed-planners-details-page__services">
            <ServicesWeProvide services={plannerDetails?.services || []} />
          </Box>

          {/* Plans / Packages Section */}
          <Box className="preneed-planners-details-page__plans-wrapper">
            <Box className="preneed-planners-details-page__plans">
              <PlansPackages
                packages={plannerDetails?.packages}
                plannerId={plannerDetails.id}
              />
            </Box>
          </Box>

          {/* Quote Form Section */}
          <Box className="preneed-planners-details-page__quote-form">
            <QuoteFormContent
              partnerId={plannerDetails?.id}
              servicesOptions={plannerDetails?.services}
              plannerServiceCategories={plannerDetails?.serviceCategories}
              requestFn={requestQuote}
              onSuccess={() => setShowSuccessModal(true)}
            />
          </Box>

          {/* Testimonials Section */}
          <Box
            ref={testimonialsRef}
            className="preneed-planners-details-page__testimonials"
            id="testimonials"
          >
            <TestimonialSection partnerId={plannerDetails?.id} />
          </Box>

          {/* Customer Reviews */}
          <Box ref={reviewsRef} className="customer-reviews">
            <ReviewsComponent
              reviews={reviews?.slice(0, 3)}
              totalReviews={plannerDetails.totalReviews}
              rating={plannerDetails.avgRating}
              isMobile={isMobile}
              partnerId={plannerDetails.id}
              onReviewSubmitted={handleReviewSubmitted}
              onReviewDeleted={handleReviewDeleted}
            />
            {reviews.length > 3 && (
              <Box display="flex" justifyContent="center" mt={2} mb={2}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate(
                      `/home/list-of-funeral-homes/details/${id}/reviews`,
                    )
                  }
                  sx={{
                    textTransform: "none",
                    borderColor: "rgba(16, 65, 69, 1)",
                    color: "rgba(16, 65, 69, 1)",
                    borderRadius: "8px",
                    width: "229px",
                    height: "48px",
                    fontSize: "16px",
                    fontWeight: 600,
                    fontFamily: "Roboto",
                  }}
                >
                  View all reviews ({reviews.length - 3})
                </Button>
              </Box>
            )}
            {reviews.length === 0 && (
              <Box textAlign="center" mt={4} mb={4}>
                <p>No reviews available for this provider yet.</p>
              </Box>
            )}
          </Box>
          {showSuccessModal && (
            <PlannerSuccessModal
              open={showSuccessModal}
              onClose={() => setShowSuccessModal(false)}
              title="Successful Message"
              subtitle="Thank you"
              businessName={plannerDetails.businessName}
              isConsultant={false}
            />
          )}
        </Container>
      )}
      {!plannerDetails && !errorMessage && (
        <Box className="preneed-planners-details-page__spinner-container">
          <CircularProgress
            size="3rem"
            className="preneed-planners-details-page__spinner"
          />
        </Box>
      )}
      {errorMessage && (
        <Box className="spinner-details-page">{errorMessage}</Box>
      )}
    </>
  );
};

export default PreneedPlannersDetailsPage;
